import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { accountsApi } from '../../api/accounts'
import { contactsApi } from '../../api/contacts'
import { useSpoke } from '../../hooks/useSpoke'
import { Avatar } from '../Avatar'

// The company or person a preset points at ("subsidiary of…", "they are really at…"). The lists are the
// same ones Accounts and Contacts read, so the names on offer are exactly the names on those screens and
// nothing new is invented here. Filtering is local: these lists are already in the cache by the time a
// person is correcting something on them.

export interface Picked {
  id: string
  name: string
}

export function EntityPicker({ targetKind, exclude, picked, onPick }: {
  targetKind: 'company' | 'person'
  /** the entity being corrected: it can never be its own parent or its own company */
  exclude?: string | null
  picked: Picked | null
  onPick: (picked: Picked | null) => void
}) {
  const spoke = useSpoke()
  const [term, setTerm] = useState('')
  const companies = useQuery({ queryKey: ['accounts', spoke], queryFn: () => accountsApi.list(spoke), staleTime: 60_000, enabled: targetKind === 'company' })
  const people = useQuery({ queryKey: ['contacts', spoke], queryFn: () => contactsApi.list(spoke), staleTime: 60_000, enabled: targetKind === 'person' })

  const options = useMemo(() => {
    const t = term.trim().toLowerCase()
    if (targetKind === 'company') {
      return (companies.data?.accounts ?? [])
        .filter((a) => a.companyId !== exclude)
        .filter((a) => !t || a.name.toLowerCase().includes(t) || (a.domain ?? '').toLowerCase().includes(t))
        .slice(0, 8)
        .map((a) => ({ id: a.companyId, name: a.name, meta: a.domain ?? '', photoUrl: null as string | null }))
    }
    return (people.data?.contacts ?? [])
      .filter((c) => c.person.personId !== exclude)
      .filter((c) => !t || c.person.name.toLowerCase().includes(t) || (c.person.email ?? '').toLowerCase().includes(t) || c.companyName.toLowerCase().includes(t))
      .slice(0, 8)
      .map((c) => ({ id: c.person.personId, name: c.person.name, meta: c.companyName, photoUrl: c.person.photoUrl ?? null }))
  }, [targetKind, term, exclude, companies.data, people.data])

  const loading = targetKind === 'company' ? companies.isPending : people.isPending

  if (picked) {
    return (
      <p className="correct-picked">
        <span className="chip on">{picked.name}</span>
        <button type="button" className="link-button small" onClick={() => onPick(null)}>Pick another</button>
      </p>
    )
  }

  return (
    <div className="correct-picker">
      <input
        className="correct-search"
        value={term}
        onChange={(ev) => setTerm(ev.target.value)}
        placeholder={targetKind === 'company' ? 'Which company?' : 'Which person?'}
        aria-label={targetKind === 'company' ? 'Find a company' : 'Find a person'}
      />
      {loading && <p className="muted small">Loading…</p>}
      {!loading && options.length === 0 && (
        <p className="muted small">{term ? 'Nothing on file matches that.' : `No ${targetKind === 'company' ? 'accounts' : 'contacts'} on file yet.`}</p>
      )}
      <ul className="correct-options">
        {options.map((o) => (
          <li key={o.id}>
            <button type="button" className="correct-option" onClick={() => onPick({ id: o.id, name: o.name })}>
              {targetKind === 'person' && <Avatar name={o.name} photoUrl={o.photoUrl} persona={null} role={null} size={22} />}
              <strong>{o.name}</strong>
              {o.meta && <span className="muted small">{o.meta}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
